<script setup>
import { invoke } from '@tauri-apps/api/core';

import './assets/tailwind.css';
import { shortcutLookup } from './utils/lookupTables';
import { HaspObjectLayer } from './draw/HaspObjectLayer';
import ScaleStage from './draw/ScaleStage';
import RulerCanvas from './draw/RulerCanvas';
import Importer from './exporter/Importer';
import { getObjectPage } from './utils/hasputils';
// import haspTheme from './Hasp/HaspTheme';

const performGreet = async () => {
    if (!window.__TAURI__) {
        alert('Please download and install the desktop app to use this function.');
    } else {
        const message = await invoke('greet', { name: 'OpenHasp user' });
        window.alert(message);
    }
};
</script>

<template>
    <!-- The app row is bounded by the window's client area: the center column
         is the only part that grows/shrinks, so nothing can push the right
         sidebar's tab strip out of view. -->
    <div class="flex h-screen w-full max-w-full overflow-hidden text-xs m-0 p-0" id="main_container">
        <LeftSidebar
            :appSettings="appSettings"
            :selectedNode="selectedNode"
            :activePage="activePage"
            :customComponents="customComponents"
            :canUndo="canUndo"
            :canRedo="canRedo"
            @add-object="addObject"
            @open-save-custom-component="openSaveCustomComponentDialog"
            @open-custom-manager="showCustomManagerPopup = true"
            @open-plates="showPlatesPopup = true"
            @open-plate-types="showPlateTypesPopup = true"
            @open-settings="showSettingsPopup = true"
            @delete="handleDeleteButton"
            @copy="handleCopyObject"
            @screenshot="pageScreenshot"
            @open-docs="showDocsPopup = true"
            @new-project="showNewProjectModal = true"
            @undo="undo"
            @redo="redo"
            @save-design="doSaveDesign"
            @open-export="showJsonlPopup = true"
            @open-hass-export="showHassExportPopup = true"
            @open-image-store="showImageTab = true"
            @open-scripts="showScriptsPopup = true"
            @open-telnet="showTelnetPopup = true"
            @open-mqtt="showMqttPopup = true"
            @open-ftp="showFTPPopup = true"
            @place-custom-component="placeCustomComponent"
            @add-logic-node="addGraphNode('logic', $event)"
            @add-hass-node="addGraphNode('hass', $event)"
            @import-file="importFile"
            @import-legacy-file="importFromPlate"
            @open-github-sync="showGitHubSyncModal = true"
        />
        <div class="justify-center items-center h-screen flex flex-col flex-1 min-w-0 overflow-hidden">
            <!-- EDIT COMPONENT DESIGN BANNER -->
            <div v-if="editingComponent"
                class="w-full flex items-center gap-3 bg-yellow-600 text-black text-xs px-3 py-1.5 flex-shrink-0">
                <span class="flex-1 min-w-0">Editing design of <strong>{{ editingComponent.name }}</strong> — select the root object on the canvas, then click Update.</span>
                <button @click="handleUpdateCustomComponentDesign"
                    class="px-3 py-1 rounded bg-black text-yellow-300 font-semibold hover:bg-gray-900 flex-shrink-0">Update</button>
                <button @click="editingComponent = null"
                    class="px-3 py-1 rounded bg-yellow-800 text-white hover:bg-yellow-900 flex-shrink-0">Cancel</button>
            </div>
            <StatusBar :status="status" :selectedNode="selectedNode" :stage_scale="stageScale"
                :designModified="designModified" :key="statusKey" @arrange-graph="arrangeGraph" />
            <div class="w-full flex-1 relative overflow-hidden" id="canvas_wrapper" style="touch-action: none;">
                <div class="absolute inset-0 rounded-md bg-container-color cursor-default" id="konva_container" style="touch-action: none;"></div>
            </div>
        </div>
        <RightPanel :showHistory="appSettings.ui.enable_history">
            <template #pages>
                <PageSelector
                    v-if="layer"
                    :objectlayer="layer"
                    :activePageId="layer?.activePage?.haspid"
                    :key="'pageselector-' + treeKey"
                    @selectPage="handlePageSelect"
                    @addPage="addPage"
                    @addTemplatePage="addTemplatePage"
                    @deletePage="handlePageDelete"
                    @reorderPages="handlePageReorder"
                />
            </template>
            <template #outline>
                <LayerTreeview v-if="layer" :objectlayer="layer" :treekey="treeKey" :settings="appSettings"
                    @node-select="onTreeNodeSelect" />
            </template>
            <template #properties>
                <div v-if="selectedNodes.length > 1"
                    class="flex items-center gap-1 px-2 py-1 mb-1 bg-blue-900/40 border border-blue-600/50 rounded text-xs text-blue-300">
                    <span>{{ selectedNodes.length }} objects selected</span>
                    <span class="text-blue-500 ml-1">(showing primary)</span>
                </div>
                <LogicNodeProperties v-if="logicSelected" :layer="layer" :settings="appSettings" />
                <HassNodeProperties v-else-if="hassSelected" :layer="layer" :settings="appSettings" />
                <PropertiesPanel v-else-if="selectedNode" v-model:node="selectedNode" :settings="appSettings"
                    :updateNode="updateNodeKey"
                    @open-dataset="openDatasetInLibrary" />
                <div v-else class="text-slate-400 text-xs text-center py-4">Select an object to view its properties</div>
            </template>
            <template #resources>
                <ResourceManagerShell
                    :registry="resourceManagerRegistry"
                    v-model:activeType="activeResourceType"
                    :context="{
                        settings: appSettings,
                        objectlayer: layer,
                        requestOpenUUID: requestedDatasetUUID,
                        onNavigateToWidget: historyNavigate,
                        imageStoreUpdated: imageStoreUpdated,
                        onImageDeleted: handleImageDeleted,
                        onImageUpload: handleImageAdded,
                    }"
                />
            </template>
            <template #themes>
                <ThemeLibraryPanel :settings="appSettings" @use-theme="useThemeInDesign" />
            </template>
            <template #history>
                <div class="flex flex-col h-full">
                    <HistoryPanel
                        class="flex-1 min-h-0"
                        :selectedUuid="selectedNode?.id?.()"
                        :selectedPageUuid="activePage?.id?.()"
                        :objectLabel="selectedNode ? (selectedNode.obj + (selectedNode.haspid ?? '')) : null"
                        @jump-to="historyJumpTo"
                        @restore-version="historyRestoreVersion"
                        @navigate="historyNavigate"
                    />
                    <div class="border-t border-white/10 shrink-0" style="height: 220px;">
                        <BookmarkManager
                            :currentDesignBytes="currentDesignBytes"
                            @create="createBookmark"
                            @restore="restoreBookmark"
                        />
                    </div>
                </div>
            </template>
        </RightPanel>
        <LogicNodePicker v-if="graphPicker" :x="graphPicker.client.x" :y="graphPicker.client.y"
            :groups="graphPicker.groups" @pick="pickGraphNode" @close="graphPicker = null" />
        <AppModals
            :isLoading="isLoading"
            :loadingMessage="loadingMessage"
            :loadingProgress="loadingProgress"
            :appSettings="appSettings"
            :layer="layer"
            :layerUpdate="layerUpdate"
            :imageStoreUpdated="imageStoreUpdated"
            :plates="plates"
            :testingMqtt="testingMqtt"
            :mqttTestStatus="mqttTestStatus"
            :mqttTestMessage="mqttTestMessage"
            :testingHass="testingHass"
            :hassTestStatus="hassTestStatus"
            :hassTestMessage="hassTestMessage"
            :testingSftp="testingSftp"
            :sftpTestStatus="sftpTestStatus"
            :sftpTestMessage="sftpTestMessage"
            v-model:showMqttPopup="showMqttPopup"
            v-model:showJsonlPopup="showJsonlPopup"
            v-model:showHassExportPopup="showHassExportPopup"
            v-model:showScriptsPopup="showScriptsPopup"
            v-model:showFTPPopup="showFTPPopup"
            v-model:showTelnetPopup="showTelnetPopup"
            v-model:showSettingsPopup="showSettingsPopup"
            v-model:showImageTab="showImageTab"
            v-model:showPlatesPopup="showPlatesPopup"
            v-model:showPlateTypesPopup="showPlateTypesPopup"
            v-model:showDeleteModal="showDeleteModal"
            v-model:showNewProjectModal="showNewProjectModal"
            v-model:showSaveDesignPrompt="showSaveDesignPrompt"
            :saveDesignFilename="saveDesignFilename"
            :designMajor="designMajor"
            :designMinor="designMinor"
            :designVersionHistory="designVersionHistory"
            v-model:showSaveCustomComponentDialog="showSaveCustomComponentDialog"
            :saveCustomComponentThumbnail="saveCustomComponentThumbnail"
            v-model:showCustomManagerPopup="showCustomManagerPopup"
            v-model:showDocsPopup="showDocsPopup"
            v-model:showImportIssuesPopup="showImportIssuesPopup"
            :importIssues="importIssues"
            :customManagerRefresh="customManagerRefresh"
            @save-settings="saveSettings"
            @update-zoom-sensitivity="updateZoomSensitivity"
            @update-connection-spacing="updateConnectionSpacing"
            @test-mqtt-connection="testMqttConnection"
            @test-hass-connection="testHassConnection"
            @test-sftp-connection="testSftpConnection"
            @load-settings-from-file="loadSettingsFromFile"
            @image-deleted="handleImageDeleted"
            @image-added="handleImageAdded"
            @update-plates="onUpdatePlates"
            @update-active-plate="onActivePlateUpdate"
            @delete-confirm="deleteItem"
            @delete-cancel="cancelDelete"
            @new-project-confirm="newProject"
            @save-design="saveDesign"
            @save-custom-component="handleSaveCustomComponent"
            @custom-components-updated="loadCustomComponents"
            @edit-design="startEditComponentDesign"
            v-model:showGitHubSyncModal="showGitHubSyncModal"
            :designId="designId"
            :designFilename="designName"
            :buildPackage="buildCurrentPackage"
            :testingGithub="testingGithub"
            :githubTestStatus="githubTestStatus"
            :githubTestMessage="githubTestMessage"
            @test-github-connection="testGithubConnection"
            @github-load-package="importPackage"
            @package-saved="handlePackageSaved"
        />
        <DatasetImportDialog
            :show="showDatasetImportDialog"
            :missing="pendingDatasetImports"
            @resolved="showDatasetImportDialog = false; pendingDatasetImports = []"
        />
    </div>
</template>

<script>
import {
    defaultSettings,
    loadSettings,
    saveAllSettings,
    readSettingsFromFile,
    writeSettingsToFile,
    getAllPlates,
    saveAllPlates,
} from './utils/store/settings';
import { setActiveTheme } from './Hasp/properties/ConfigManager.js';
import { loadExternalThemes } from './themes/themeRegistry.js';
import { interpolateColor } from './utils/color.js';
import { getAllPlateTypes } from './utils/plateTypeDB';
import Modal from './components/Modal.vue';
import PropertiesPanel from './components/properties/PropertiesPanel.vue';
import LogicNodeProperties from './components/logic/LogicNodeProperties.vue';
import LogicNodePicker from './components/logic/LogicNodePicker.vue';
import HassNodeProperties from './components/hass/HassNodeProperties.vue';
import { findPaletteEntry } from './components/logic/paletteDrag.js';
import { CANVAS_GRAPHS, canvasGraphOfDrag } from './components/graph/canvasGraphs.js';
import Accordion from './components/Accordion.vue';
import StatusBar from './components/StatusBar.vue';
import LayerTreeview from './components/LayerTreeview.vue';
import PageSelector from './components/PageSelector.vue';

import json2jsonl from './utils/jsonl';
import Exporter from './exporter/Exporter';
import JSZip from 'jszip';
import { DesignPackage } from './package/DesignPackage.js';
import { buildDesignPackage } from './package/DesignPackageBuilder.js';
import { loadDesignPackage } from './package/DesignPackageLoader.js';
import { legacyFileToPackage } from './package/LegacyPlateImporter.js';
import mqttService from './services/MqttService';
import { getAllCustomComponents, addCustomComponent, updateCustomComponent } from './utils/customComponentDB.js';
import { v4 as uuidv4 } from 'uuid';
import RightPanel from './components/RightPanel.vue';
import HistoryPanel from './components/HistoryPanel.vue';
import ResourceManagerShell from './components/resources/ResourceManagerShell.vue';
import { RESOURCE_UI_REGISTRY } from './components/resources/resourceUIRegistry.js';
import DatasetImportDialog from './components/datasets/DatasetImportDialog.vue';
import { useDatasetStore } from './stores/datasetStore.js';
import { useAudioStore } from './stores/audioStore.js';
import { useFontStore } from './stores/fontStore.js';
import { useSynthStore } from './stores/synthStore.js';
import { useUiLayoutStore } from './stores/uiLayoutStore.js';
import { useLogicStore } from './stores/logicStore.js';
import { useHassFlowStore } from './stores/hassFlowStore.js';
import { useCanvasModeStore } from './stores/canvasModeStore.js';
import gpioMetadataProvider from './gpio/GpioMetadataProvider.js';
import { copyObjectLogic, exportObjectTreeLogic } from './logic/logicClipboard.js';
import { copyObjectHass, exportObjectTreeHass } from './hass/hassClipboard.js';
import BookmarkManager from './components/BookmarkManager.vue';
import LeftSidebar from './components/LeftSidebar.vue';
import AppModals from './components/AppModals.vue';
import ThemeLibraryPanel from './themes/ThemeLibraryPanel.vue';

import { useUndoStore } from './history/undoStore';
import { useBookmarkStore } from './history/bookmarkStore';
import RestoreBookmarkCommand from './history/commands/RestoreBookmarkCommand.js';
import { setLayer } from './history/layerService';
import ObjectMoveCommand from './history/commands/ObjectMoveCommand.js';
import ObjectResizeCommand from './history/commands/ObjectResizeCommand.js';
import PropertyChangeCommand from './history/commands/PropertyChangeCommand.js';
import ObjectCreateCommand from './history/commands/ObjectCreateCommand.js';
import ObjectDeleteCommand, { snapshotObjectConfig } from './history/commands/ObjectDeleteCommand.js';
import PageCreateCommand from './history/commands/PageCreateCommand.js';
import PageDeleteCommand, { snapshotPage } from './history/commands/PageDeleteCommand.js';
import BatchCommand from './history/commands/BatchCommand.js';
import RestoreObjectVersionCommand, { reconstructObjectState } from './history/commands/RestoreObjectVersionCommand.js';
import { capturePropertyState } from './history/nodeUtils.js';
import { startImportSession, endImportSession, getImportIssues } from './utils/importIssues';
import { setConnectionSpacing } from './draw/connectionResolver';
import { normalizeConnectionCreationDefaults } from './draw/connectionCreationDefaults.js';
import { normalizeRulerScale } from './draw/rulerScale.js';

export default {
    components: {
        Modal,
        Accordion,
        StatusBar,
        LayerTreeview,
        PageSelector,
        PropertiesPanel,
        LogicNodeProperties,
        HassNodeProperties,
        LogicNodePicker,
        RightPanel,
        HistoryPanel,
        BookmarkManager,
        LeftSidebar,
        AppModals,
        ThemeLibraryPanel,
        ResourceManagerShell,
        DatasetImportDialog,
    },
    data() {
        return {
            resourceManagerRegistry: RESOURCE_UI_REGISTRY,
            activeResourceType: 'dataset',
            appSettings: { ...defaultSettings },
            mainContainer: undefined,
            container: undefined,
            stage: undefined,
            stageScale: 7,
            layer: undefined,
            activePage: undefined,
            isLoading: false,
            loadingMessage: 'loading...',
            loadingProgress: null,
            // showExportModal: false,
            showDeleteModal: false,
            // Node choices for a graph wire released on empty canvas: { graphKey, groups, … }.
            graphPicker: null,
            showImageTab: false,
            showNewProjectModal: false,
            showMqttPopup: false,
            showJsonlPopup: false,
            showFTPPopup: false,
            showTelnetPopup: false,
            showHassExportPopup: false,
            showHassBuilderPopup: false,
            showScriptsPopup: false,
            showSettingsPopup: false,
            showPlatesPopup: false,
            showPlateTypesPopup: false,
            showSaveDesignPrompt: false,
            saveDesignFilename: '',
            designId: null, // manifest.id — the package's stable identity, independent of designName/its storage path
            currentPackage: null, // last loaded/saved DesignPackage — carries forward unknown files/manifest keys on the next save
            designName: '',
            designMajor: 0,
            designMinor: 0,
            designVersionHistory: [],
            selectedNode: undefined,
            selectedNodes: [],   // all objects in current multi-selection
            // theme: new haspTheme(),
            status: 'Start by adding a page',
            treeKey: 0,
            statusKey: 0,
            layerUpdate: 0,
            updateNodeKey: 0,
            plates: undefined,
            plateTypes: undefined,
            imageStoreUpdated: 0,
            // Custom components
            customComponents: [],
            showSaveCustomComponentDialog: false,
            saveCustomComponentThumbnail: null,
            showCustomManagerPopup: false,
            showDocsPopup: false,
            showImportIssuesPopup: false,
            importIssues: [],
            customManagerRefresh: 0,
            editingComponent: null,
            requestedDatasetUUID: null,
            showDatasetImportDialog: false,
            pendingDatasetImports: [],
            _suppressSnapshot: true,
            designDirtyOverride: false,
            savedHistorySignature: '-1|',
            _propChangeTimer: null,
            // Connection test states
            testingMqtt: false,
            mqttTestStatus: null,
            mqttTestMessage: '',
            testingHass: false,
            hassTestStatus: null,
            hassTestMessage: '',
            testingSftp: false,
            sftpTestStatus: null,
            sftpTestMessage: '',
            // GitHub sync
            showGitHubSyncModal: false,
            testingGithub: false,
            githubTestStatus: null,
            githubTestMessage: '',
        };
    },
    computed: {
        // Active right tab lives in the shared sidebar layout store; assigning
        // here switches the tab without touching the collapsed state.
        rightPanelTab: {
            get() { return useUiLayoutStore().activeRightTab; },
            set(tab) { useUiLayoutStore().showRightTab(tab); },
        },
        canUndo() { return useUndoStore().canUndo; },
        canRedo() { return useUndoStore().canRedo; },
        // Everything that changes what the logic overlay shows; undo/redo
        // rewrites page.logic outside the store, hence historyVersion.
        logicSelected() { return Boolean(useLogicStore().selection); },
        hassSelected() { return Boolean(useHassFlowStore().selection); },
        canvasMode() { return useCanvasModeStore().mode; },
        logicRenderKey() {
            const logic = useLogicStore();
            return [logic.visible, logic.selection?.kind, logic.selection?.id, logic.revision,
                useUndoStore().historyVersion].join('|');
        },
        hassRenderKey() {
            const hass = useHassFlowStore();
            return [hass.visible, hass.selection?.kind, hass.selection?.id, hass.revision,
                useUndoStore().historyVersion].join('|');
        },
        designModified() {
            const undoStore = useUndoStore();
            // Touch historyVersion so this computed value is invalidated when
            // a move command is merged into the existing last entry.
            undoStore.historyVersion;
            return this.designDirtyOverride || this.historySignature() !== this.savedHistorySignature;
        },
        currentDesignBytes() {
            if (!this.layer) return 0;
            try {
                const exporter = new Exporter();
                const objects = exporter.doExport(this.appSettings, this.layer, false, true);
                return new TextEncoder().encode(JSON.stringify(objects)).length;
            } catch { return 0; }
        },
    },
    created() { },
    beforeUnmount() {
        this._canvasObserver?.disconnect();
        if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize);
    },
    mounted: function () {
        this.container = document.querySelector('#konva_container');
        // Ensure the container exists
        if (!this.container) {
            console.error('Container #konva_container not found');
            return;
        }
        this.mainContainer = document.querySelector('#main_container');
        const stage = new ScaleStage({
            container: 'konva_container',
            width: this.container.offsetWidth,
            height: this.container.offsetHeight,
            draggable: true,
            zoom_sensitivity: this.appSettings.ui.zoom_sensitivity,
            callback: this.stageCallback,
        });
        this.stage = stage;
        this.addNewLayer();

        // Rulers — mounted after addObjectLayer so guidelineManager exists
        const wrapper = document.querySelector('#canvas_wrapper');
        const ruler = new RulerCanvas({
            wrapperEl: wrapper,
            stage,
            guidelineManager: stage.guidelineManager,
            scale: this.appSettings.ui.ruler_scale,
        });
        stage.rulerCanvas = ruler;
        ruler.update();

        // Follow the canvas container's own size instead of window resizes:
        // that covers window resizing AND every internal layout change (right
        // sidebar drag/collapse) without any component having to fake a
        // window resize event. #konva_container is absolutely positioned inside
        // #canvas_wrapper, so resizing the stage inside it can never change the
        // observed element's box — the dependency stays one-way. The integer
        // guard in fitStageIntoContainer() additionally stops a fractional
        // measurement from ping-ponging between two layouts.
        if (typeof ResizeObserver !== 'undefined') {
            this._canvasObserver = new ResizeObserver(() => this.fitStageIntoContainer());
            this._canvasObserver.observe(this.container);
        } else {
            this._onWindowResize = () => this.fitStageIntoContainer();
            window.addEventListener('resize', this._onWindowResize);
        }

        this.loadCustomComponents();

        loadSettings()
            .then((settings) => {
                this.isLoading = true;
                this.appSettings = settings;
                // Apply the persisted settings theme even when there are no
                // saved plates yet. Otherwise the first canvas objects use
                // ConfigManager's fallback theme.
                setActiveTheme(
                    settings.plate?.hasp_theme,
                    settings.plate?.primary_color,
                    settings.plate?.secondary_color,
                );
                // Resource libraries load before any design does: designs reference them.
                Promise.all([
                    getAllPlates(), getAllPlateTypes(), loadExternalThemes(),
                    useDatasetStore().load(), useAudioStore().load(),
                    useFontStore().load(), useSynthStore().load(),
                ]).then(([plates, plateTypes]) => {
                    this.plates = plates;
                    this.plateTypes = plateTypes;
                    // Apply the active plate's theme BEFORE creating pages/objects
                    const activePlate = plates.find(p => p.name === settings.plate?.name) ?? plates[0];
                    if (activePlate) {
                        const activePlateType = plateTypes.find(pt => pt.boardname === activePlate.size_name);
                        this.appSettings.plate = {
                            ...activePlate,
                            width: activePlateType?.screenSize?.width ?? activePlate.width,
                            height: activePlateType?.screenSize?.height ?? activePlate.height,
                            shape: activePlateType?.shape ?? activePlate.shape ?? 'rectangular',
                            native: activePlate.native ?? false,
                            http_port: activePlate.http_port ?? settings.plate?.http_port ?? 8080,
                            telnet_port: activePlate.telnet_port ?? settings.plate?.telnet_port ?? 2323,
                        };
                        setActiveTheme(activePlate.hasp_theme, activePlate.primary_color, activePlate.secondary_color);
                    } else {
                        // The theme registry has finished loading at this
                        // point, so this also resolves a persisted custom
                        // theme when the user has not created a plate yet.
                        setActiveTheme(settings.plate?.hasp_theme, settings.plate?.primary_color, settings.plate?.secondary_color);
                    }
                    this.setPageSize();
                    this.designId = uuidv4();
                    this.currentPackage = null;
                    this.layer.addPage({ obj: 'page', page: 0 });
                    this.addPage();
                    this.centerPlate();
                    this._suppressSnapshot = false;
                    useUndoStore().clear();
                    this.markDesignSaved();
                });

                this.updateUiFromSettings('all');
            })
            .catch((error) => {
                console.error('Error loading settings:', error);
            })
            .finally(() => {
                this.isLoading = false; // Indicate that loading is complete
            });

        this.selectedNode = undefined;
        // make it focusable
        this.container.tabIndex = 1;
        // focus it also stage will be in focus on its click
        this.container.focus();
        this.container.addEventListener('keydown', this.handleCanvasKey);
        this.container.addEventListener('dragover', this.onGraphDragOver);
        this.container.addEventListener('drop', this.onGraphDrop);
    },
    beforeDestroy() {
        // Remove the event listener when the app component is about to be destroyed
        this.container.removeEventListener('keydown', this.handleCanvasKey);
        this.container.removeEventListener('dragover', this.onGraphDragOver);
        this.container.removeEventListener('drop', this.onGraphDrop);
    },
    watch: {
        selectedNode: function () {
            // this.treeKey++;
            this.statusKey++;
        },
        // doPropertiesUpdate: function () {
        //     this.treeKey++;
        //     this.statusKey++;
        // },
        treeKey: function () { },
        logicRenderKey() {
            const logic = useLogicStore();
            this.stage?.logicLayer?.setState({ visible: logic.visible, selection: logic.selection });
        },
        hassRenderKey() {
            const hass = useHassFlowStore();
            this.stage?.hassLayer?.setState({ visible: hass.visible, selection: hass.selection });
        },
        // A graph selection only lives in its own mode.
        canvasMode(mode) {
            if (mode !== 'logic') useLogicStore().clearSelection();
            if (mode !== 'hass') useHassFlowStore().clearSelection();
        },
    },
    methods: {
        async undo() {
            await useUndoStore().undo();
            this._afterHistoryOp();
        },
        async redo() {
            await useUndoStore().redo();
            this._afterHistoryOp();
        },
        async historyJumpTo(index) {
            await useUndoStore().jumpTo(index);
            this._afterHistoryOp();
        },
        historySignature() {
            const undoStore = useUndoStore();
            const currentEntries = undoStore.entries.slice(0, undoStore.cursor + 1);
            return `${undoStore.cursor}|${currentEntries.map(entry => `${entry.id ?? ''}:${entry.historyRevision ?? ''}`).join(',')}`;
        },
        markDesignDirty() {
            if (!this._suppressSnapshot) this.designDirtyOverride = true;
        },
        markDesignSaved() {
            this.designDirtyOverride = false;
            this.savedHistorySignature = this.historySignature();
        },
        handlePackageSaved(pkg) {
            this.currentPackage = pkg;
            this.markDesignSaved();
        },

        /**
         * Navigate to the page and object referenced by a history entry without
         * changing the history cursor. Called on single-click in HistoryPanel.
         */
        onTreeNodeSelect(_node) {
            // Intentionally no tab switch — the user stays on whatever panel is active.
        },

        // Widget → Dataset: opens the Dataset Library tab and that dataset's
        // editor/inspector popup. Mirror of historyNavigate's direction.
        openDatasetInLibrary(uuid) {
            useUiLayoutStore().revealRightTab('resources');
            this.activeResourceType = 'dataset';
            // Force a null→uuid transition so the watcher in DatasetManager
            // fires even when re-opening the same dataset that's already set.
            this.requestedDatasetUUID = null;
            this.$nextTick(() => { this.requestedDatasetUUID = uuid; });
        },
        historyNavigate({ objectUuid, pageUuid }) {
            if (!this.layer) return;

            // 1. Switch to the referenced page if it exists and is not already active
            if (pageUuid) {
                const page = this.layer.findOne('#' + pageUuid);
                if (page && page !== this.layer.activePage) {
                    this.layer.switchToPage(page);
                    this.activePage = page;
                    this.treeKey++;
                }
            }

            // 2. Find and select the referenced object
            if (objectUuid) {
                const node = this.layer.findOne('#' + objectUuid);
                if (node && !node.isType?.('page') && !node.isType?.('template_page')) {
                    // Reuse the same selection path the tree uses — switches page if
                    // needed and fires 'node-selected' so App.vue keeps in sync.
                    // Does NOT change rightPanelTab (only 'mouse-down' does that).
                    this.layer.setSelectedNode(node);
                    this.statusKey++;
                    this.updateNodeKey++;
                }
                // Object not found at current history position — it may have been
                // deleted or not yet created. Page switch above is still applied.
            }
        },
        historyRestoreVersion(targetIndex) {
            const node = this.selectedNode;
            if (!node) return;
            const entries = useUndoStore().entries;
            const restoredState = reconstructObjectState(entries, targetIndex, node.id());
            if (!restoredState) return;
            const currentState = capturePropertyState(node);
            useUndoStore().push(new RestoreObjectVersionCommand({
                objectUuid: node.id(),
                pageUuid: getObjectPage(node)?.id(),
                restoredState,
                currentState,
            }));
            this._afterHistoryOp();
        },
        /** Capture the full current design as a raw JSONL string (for bookmarks). */
        _designSnapshotRaw() {
            if (!this.layer) return '';
            const exporter = new Exporter();
            // includeHass=true ensures hass_config is part of every bookmark snapshot
            const exportData = exporter.doExport(this.appSettings, this.layer, true, true);
            return json2jsonl(exportData);
        },

        /** Create a new bookmark from the current design state. */
        createBookmark(name, description = '') {
            const raw = this._designSnapshotRaw();
            if (!raw) return;
            useBookmarkStore().addBookmark(name, raw, description);
            this.markDesignDirty();
        },

        /** Restore a bookmark by id — creates a RestoreBookmarkCommand in global history. */
        async restoreBookmark(bookmarkId) {
            const rawAfter = useBookmarkStore().getSnapshot(bookmarkId);
            if (!rawAfter || !this.layer) return;
            const rawBefore = this._designSnapshotRaw();
            const restoreFn = async (snapshot) => {
                const importer = new Importer();
                await importer.doImportFromString(snapshot, this.layer, async ({ progress }) => {
                    this.loadingProgress = progress;
                    await this.$nextTick();
                });
                if (!this.layer.pageZero) {
                    const prevPage = this.layer.activePage;
                    this.layer.addPage({ obj: 'page', page: 0 });
                    if (prevPage) this.layer.switchToPage(prevPage);
                }
                this.activePage = this.layer.activePage;
                await this.$nextTick();
                this.treeKey++;
                this.statusKey++;
                this.updateNodeKey++;
            };
            const bookmark = useBookmarkStore().bookmarks.find(b => b.id === bookmarkId);
            const cmd = new RestoreBookmarkCommand(bookmark?.name ?? 'Bookmark', rawBefore, rawAfter, restoreFn);
            this.isLoading = true;
            this.loadingMessage = 'Restoring bookmark…';
            this.loadingProgress = 0;
            await cmd.execute();
            this.loadingProgress = 1;
            this.isLoading = false;
            useUndoStore().push(cmd);
            this._afterHistoryOp();
        },

        async _afterHistoryOp() {
            // Cancel any pending property-change debounce so it doesn't fire after undo/redo
            clearTimeout(this._propChangeTimer);
            this._propChangeTimer = null;
            this._propChangeBefore = null;
            if (this.layer) {
                this.layer.transformer?.nodes([]);
                this.layer.haspobject = this.layer.activePage;
                this.layer._beforeColors = null;
                this.layer._historySuppress = false;
            }
            this.selectedNode = undefined;
            this.activePage = this.layer?.activePage;
            this.layer?.batchDraw();
            await this.$nextTick();
            this.treeKey++;
            this.statusKey++;
            this.updateNodeKey++;
        },
        setupUI() { },
        addNewLayer() {
            const layer = new HaspObjectLayer({
                x: 0,
                y: 0,
                draggable: false,
                connection_defaults: this.appSettings.ui.object_defaults?.connection,
                connection_max_speed: this.appSettings.ui.connection_max_speed,
                callback: this.layerCallback,
            });
            this.layer = layer;
            setConnectionSpacing(layer, this.appSettings.ui.connection_spacing);
            
            setLayer(layer);
            layer.showObjectId(this.appSettings.ui.show_hasp_id);

            this.setPageSize();
            this.stage.addObjectLayer(this.layer);
            for (const graph of Object.values(CANVAS_GRAPHS)) {
                graph.layerOf(this.stage)?.setHandlers({
                    onSelect: (kind, id) => this.selectGraph(graph.key, kind, id),
                    onConnect: (from, to) => {
                        const result = graph.useStore().connect(this.layer.activePage, from, to);
                        if (result.error) this.status = result.error;
                    },
                    onMove: (id, x, y) => graph.useStore().moveNode(this.layer.activePage, id, x, y),
                    onWireToEmpty: drop => {
                        const groups = drop.port ? graph.paletteForWire(drop.dir, drop.port) : [];
                        this.graphPicker = groups.length ? { ...drop, groups, graphKey: graph.key } : null;
                    },
                });
            }

            this.selectedNode = undefined;

            this.layer.showObjectDetails(this.appSettings.ui.show_object_details);
            this.layer.redrawOverlay = this.appSettings.ui.page0_overlay;
            
            // Enable single page mode by default
            this.layer.singlePageMode = true;
        },
        setPageSize: function () {
            // var plate_size = lookupPageSize['small_landscape'];
            // if (this.appSettings.plate) {
            //     plate_size = {width:this.appSettings.plate.width, height: this.appSettings.plate.height};
            // }
            if (this.layer) {
                this.layer.pageWidth = this.appSettings.plate.width;
                this.layer.pageHeight = this.appSettings.plate.height;
                this.layer.updatePageDisplayShape?.(this.appSettings.plate.shape);
                this.layer.updateOffPageMask?.();
            }
        },
        onActivePlateUpdate(plate) {
            if (plate) {
                this.appSettings.plate = { ...this.appSettings.plate, ...plate };
                // A changed plate GPIO configuration updates every GPIO entity picker.
                gpioMetadataProvider.setSettings(this.appSettings);
                setActiveTheme(
                    this.appSettings.plate.hasp_theme,
                    this.appSettings.plate.primary_color,
                    this.appSettings.plate.secondary_color,
                );
                this.setPageSize();
            }

            // Theme changes must re-run every widget's existing style
            // listeners. This updates both the canvas and the values exposed
            // by the Property Editor without mutating design overrides.
            this.layer?.find?.(node => typeof node.refreshAllStyles === 'function')
                .forEach(node => node.refreshAllStyles());
            this.layer?.requestRender?.();
            this.updateNodeKey++;
            this.statusKey++;
            this.saveSettings('active-plate');
        },
        useThemeInDesign(theme) {
            if (!theme?.selection) return;
            this.onActivePlateUpdate({
                ...this.appSettings.plate,
                hasp_theme: theme.selection,
                hasp_theme_num: null,
            });
        },
        saveSettings(type) {
            if (type === 'active-plate' && this.appSettings.tauri) {
                mqttService.connectToBroker(this.appSettings);
            }
            if (type === 'ui' && this.layer) {
                const defaults = normalizeConnectionCreationDefaults(
                    this.appSettings.ui.object_defaults?.connection,
                );
                this.appSettings.ui.object_defaults ??= {};
                this.appSettings.ui.object_defaults.connection = defaults;
                this.layer.connectionDefaults = defaults;
                this.layer.setConnectionMaxSpeed(this.appSettings.ui.connection_max_speed);
            }
            saveAllSettings(this.appSettings, type);
            this.markDesignDirty();
            this.updateUiFromSettings('all');
            if (!this.appSettings.ui.enable_history) {
                useUndoStore().clear();
                if (this.rightPanelTab === 'history') this.rightPanelTab = 'pages';
            }
        },
        updateZoomSensitivity() {
            this.stage.setZoomSensitivity(this.appSettings.ui.zoom_sensitivity);
            this.saveSettings('ui');
        },
        updateConnectionSpacing() {
            this.appSettings.ui.connection_spacing = setConnectionSpacing(
                this.layer,
                this.appSettings.ui.connection_spacing,
            );
            this.saveSettings('ui');
        },
        async testMqttConnection() {
            this.testingMqtt = true;
            this.mqttTestStatus = null;
            this.mqttTestMessage = '';

            try {
                const result = await invoke('mqtt_test_connection_command', {
                    connArgs: {
                        protocol: this.appSettings.mqtt.protocol,
                        host: this.appSettings.mqtt.host,
                        port: String(this.appSettings.mqtt.port),
                        username: this.appSettings.mqtt.username,
                        password: this.appSettings.mqtt.password,
                    }
                });
                this.mqttTestStatus = 'success';
                this.mqttTestMessage = result;
            } catch (error) {
                this.mqttTestStatus = 'error';
                this.mqttTestMessage = error.toString();
            } finally {
                this.testingMqtt = false;
            }
        },
        async testHassConnection() {
            this.testingHass = true;
            this.hassTestStatus = null;
            this.hassTestMessage = '';

            try {
                const hassurl = `${this.appSettings.hass.protocol}://${this.appSettings.hass.host}:${this.appSettings.hass.port}`;
                const result = await invoke('hass_test_connection_command', {
                    hassurl: hassurl,
                    token: this.appSettings.hass.token,
                });
                this.hassTestStatus = 'success';
                this.hassTestMessage = result;
            } catch (error) {
                this.hassTestStatus = 'error';
                this.hassTestMessage = error.toString();
            } finally {
                this.testingHass = false;
            }
        },
        async testSftpConnection() {
            this.testingSftp = true;
            this.sftpTestStatus = null;
            this.sftpTestMessage = '';

            try {
                const result = await invoke('sftp_test_connection_command', {
                    connArgs: {
                        host: this.appSettings.hass.host,
                        port: Number(this.appSettings.hass.sftp_port),
                        user: this.appSettings.hass.sftp_user,
                        password: this.appSettings.hass.sftp_pass,
                        timeout: 10,
                    },
                });
                this.sftpTestStatus = 'success';
                this.sftpTestMessage = result;
            } catch (error) {
                this.sftpTestStatus = 'error';
                this.sftpTestMessage = error.toString();
            } finally {
                this.testingSftp = false;
            }
        },
        async testGithubConnection() {
            this.testingGithub = true;
            this.githubTestStatus = null;
            this.githubTestMessage = '';
            try {
                const { testConnection } = await import('./github/api.js');
                const info = await testConnection(this.appSettings.github);
                this.githubTestStatus = 'success';
                this.githubTestMessage = `Connected to ${info.full_name} (${info.visibility})`;
            } catch (e) {
                this.githubTestStatus = 'error';
                this.githubTestMessage = e.message;
            } finally {
                this.testingGithub = false;
            }
        },
        /**
         * Builds the full current design as a DesignPackage — the one entry
         * point every transport (local .zip, browser GitHub, Tauri) uses to
         * serialize a project. Defaults existingPackage to the last
         * loaded/saved package so unknown files/manifest keys carry forward
         * automatically.
         */
        async buildCurrentPackage({ name, description, author, generatePreview = true, existingPackage = this.currentPackage } = {}) {
            return buildDesignPackage({
                settings: this.appSettings,
                layer: this.layer,
                designId: this.designId,
                name: name ?? (this.designName || this.appSettings.plate.name),
                // description/author left undefined unless the caller passes
                // them — buildDesignPackage() falls back to existingPackage's
                // manifest rather than blanking it (see its own comment).
                description,
                author,
                versionHistory: this.designVersionHistory,
                guidelines: this.stage.guidelineManager.exportGuidelines(),
                bookmarks: useBookmarkStore().toProjectData(),
                datasets: useDatasetStore().toSnapshots(this._collectReferencedDatasetUUIDs()),
                previewNode: generatePreview ? this.layer.activePage : null,
                generatePreview,
                existingPackage,
            });
        },
        updateUiFromSettings(type) {
            // Update the UI based on the type of setting changed
            const mc = this.appSettings.ui.main_color;
            const root = document.documentElement;
            root.style.setProperty('--ui-bg',       mc);
            root.style.setProperty('--ui-bg-input', interpolateColor(mc, '#ffffff', 0.12));
            root.style.setProperty('--ui-active',   interpolateColor(mc, '#00ffff', 0.55));
            this.mainContainer.style.backgroundColor = mc;
            this.container.style.backgroundColor = this.appSettings.ui.canvas_color;
            this.stage.setZoomSensitivity(this.appSettings.ui.zoom_sensitivity);
            this.appSettings.ui.ruler_scale = normalizeRulerScale(this.appSettings.ui.ruler_scale);
            this.stage.rulerCanvas?.setScale(this.appSettings.ui.ruler_scale);
            this.layer.showObjectDetails(this.appSettings.ui.show_object_details);
            this.setPageSize();
            this.layer.showObjectId(this.appSettings.ui.show_hasp_id);
            this.layer.redrawOverlay = this.appSettings.ui.page0_overlay;
            this.layer.updatePageZeroOverlay(type);
            this.stage.guidelineManager.setStickyGuidelines(this.appSettings.ui.sticky_guidelines);
        },
        // Returns the set of dataset UUIDs referenced by widgets in the current design.
        _collectReferencedDatasetUUIDs() {
            const uuids = new Set()
            if (!this.layer) return uuids
            const scan = (node) => {
                if (node.dataset) uuids.add(node.dataset)
                if (typeof node.getChildren === 'function') {
                    node.getChildren().forEach(scan)
                }
            }
            scan(this.layer)
            return uuids
        },
        centerPlate() {
            const scale  = this.stage.scaleX();
            const x = (this.stage.width()  - this.appSettings.plate.width  * scale) / 2;
            const y = (this.stage.height() - this.appSettings.plate.height * scale) / 2;
            this.stage.position({ x, y });
            this.stage.batchDraw();
            this.layer?.updateOffPageMask?.();
            this.stage.rulerCanvas?.update();
        },
        async loadSettingsFromFile(file) {
            try {
                this.appSettings = await readSettingsFromFile(file);
                this.appSettings.ui ??= {};
                this.appSettings.ui.object_defaults ??= {};
                this.appSettings.ui.object_defaults.connection = normalizeConnectionCreationDefaults(
                    this.appSettings.ui.object_defaults.connection,
                );
                if (this.layer) {
                    this.appSettings.ui.connection_spacing = setConnectionSpacing(
                        this.layer,
                        this.appSettings.ui.connection_spacing,
                    );
                    this.layer.setConnectionMaxSpeed(this.appSettings.ui.connection_max_speed);
                }
                this.saveSettings();
                this.updateUiFromSettings('all');
                this.setPageSize();
            } catch (e) {
                alert('Failed to load settings:', e);
            }
        },
        async saveSettingsToFile() {
            await writeSettingsToFile();
        },
        doSaveDesign(e) {
            this.saveDesignFilename = this.designName || this.appSettings.plate.name;
            this.showSaveDesignPrompt = true;
        },
        // Local "Save" — and the spec's "Export" — both just download the
        // current DesignPackage as a zip. There is no standalone .jsonl design
        // save: device JSONL is a separate operation (see the Export modal).
        async saveDesign({ filename, major, minor, comment }) {
            try {
                const versionedName = `${filename}_v${major}.${minor}`;
                const versionEntry = { major, minor, date: new Date().toISOString(), name: versionedName, comment };
                const updatedHistory = [...this.designVersionHistory, versionEntry];

                const pkg = await buildDesignPackage({
                    settings: this.appSettings,
                    layer: this.layer,
                    designId: this.designId,
                    name: filename,
                    versionHistory: updatedHistory,
                    guidelines: this.stage.guidelineManager.exportGuidelines(),
                    bookmarks: useBookmarkStore().toProjectData(),
                    datasets: useDatasetStore().toSnapshots(this._collectReferencedDatasetUUIDs()),
                    previewNode: this.layer.activePage,
                    existingPackage: this.currentPackage,
                });

                await this._downloadPackageAsZip(pkg, versionedName);

                this.currentPackage = pkg;
                this.designName = filename;
                this.designMajor = major;
                this.designMinor = minor;
                this.designVersionHistory = updatedHistory;
                this.markDesignSaved();
                this.status = `Design saved as ${versionedName}.zip`;
            } catch (error) {
                console.error('Failed to save design:', error);
                this.status = `Save failed: ${error?.message ?? String(error)}`;
            }
        },
        async _downloadPackageAsZip(pkg, zipBaseName) {
            const zip = new JSZip();
            for (const { path, content, encoding } of pkg.toFileList()) {
                zip.file(path, content, encoding === 'base64' ? { base64: true } : {});
            }
            const blob = await zip.generateAsync({ type: 'blob' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${zipBaseName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },
        // doExportData(e) {
        //     this.showExportModal = true;
        // },
        async callback({ progress, message }) {
            // if (result.name) this.plateName = result.name;
            // if (result.page_size) this.pageSize = result.page_size;
            this.loadingProgress = progress;
            this.loadingMessage = message;
            await this.$nextTick();
            await new Promise((resolve) => setTimeout(resolve, 50));
            this.treeKey++;
            this.statusKey++;
        },
        updateCallback(event) {
            this.treeKey++;
            this.statusKey++;
        },
        // Reads a design package zip — the Designer's own project format —
        // into a DesignPackage and imports it.
        async importFile(file) {
            if (!file) {
                return 'Selecteer een bestand om deze te importeren.';
            }
            let pkg;
            try {
                const zip = await JSZip.loadAsync(file);
                const entries = [];
                for (const relPath of Object.keys(zip.files)) {
                    const zipEntry = zip.files[relPath];
                    if (zipEntry.dir) continue;
                    const isText = relPath.endsWith('.json');
                    const content = await zipEntry.async(isText ? 'string' : 'base64');
                    entries.push({ path: relPath, content, encoding: isText ? 'utf8' : 'base64', mime: null });
                }
                pkg = DesignPackage.fromFileList(entries);
            } catch (error) {
                console.error('Failed to read design package:', error);
                this.loadingMessage = 'Import failed';
                return;
            }
            await this.importPackage(pkg);
        },
        // "Import from Plate" — the one legacy exception to the package-only
        // rule: accepts a raw openHASP plate export or a legacy pre-package
        // Designer save, both plain JSONL, as pulled directly off a device.
        // This is a legacy IMPORT PATH only, not a legacy storage format — the
        // file is converted into an ordinary DesignPackage (fresh id, no
        // manifest history) and handed to the exact same importPackage()
        // pipeline as any .zip, so the design is managed as a package from
        // then on and the next save writes a design package.
        async importFromPlate(file) {
            if (!file) return;
            try {
                const rawText = await file.text();
                const pkg = legacyFileToPackage(rawText, file.name);
                await this.importPackage(pkg);
            } catch (error) {
                console.error('Failed to import legacy plate file:', error);
                this.loadingMessage = 'Import failed';
            }
        },
        // Loads a DesignPackage into the live editor session — transactional
        // resource import (see DesignPackageLoader.js), then design.json. The
        // one shared entry point for every import source (local .zip, browser
        // GitHub, Tauri, Import from Plate): the currently selected plate
        // always defines page resolution/theme — never a package's own
        // embedded plate (see Importer._handleConfig()'s comment) — so it's
        // (re)applied here, once, rather than left to each caller.
        async importPackage(pkg) {
            this._suppressSnapshot = true;
            this.loadingMessage = 'Loading design package...';
            this.isLoading = true;
            this.loadingProgress = 0;
            await this.$nextTick();
            await new Promise((resolve) => setTimeout(resolve, 50));

            try {
                this.setPageSize();
                // Clear existing guidelines before import so stale ones don't persist
                this.stage.guidelineManager.clearGuidelines();

                const callback = async ({ progress, message }) => {
                    this.loadingProgress = progress;
                    this.loadingMessage = message;
                    await this.$nextTick();
                    await new Promise((resolve) => setTimeout(resolve, 50));
                };

                startImportSession();
                const { id, versionHistory, guidelines, bookmarks, datasets, connectionSpacing } =
                    await loadDesignPackage(pkg, this.layer, callback);

                this.currentPackage = pkg;
                this.designId = id || uuidv4();
                this.designName = pkg.manifest.name || '';
                this.designVersionHistory = versionHistory ?? [];
                if (connectionSpacing !== undefined) {
                    this.appSettings.ui.connection_spacing = connectionSpacing;
                }
                const last = this.designVersionHistory[this.designVersionHistory.length - 1];
                this.designMajor = last?.major ?? last?.version ?? 0;
                this.designMinor = last?.minor ?? 0;
                useBookmarkStore().loadFromProject(bookmarks ?? []);
                for (const snapshot of (datasets ?? [])) {
                    useDatasetStore().importSnapshot(snapshot);
                }

                // Backwards compat: older files may not include a page 0 entry
                if (!this.layer.pageZero) {
                    const prevPage = this.layer.activePage;
                    this.layer.addPage({ obj: 'page', page: 0 });
                    if (prevPage) this.layer.switchToPage(prevPage);
                }
                this.activePage = this.layer.activePage;

                // Restore guidelines after pages are loaded so setActivePageId is already
                // set to the final active page, giving importGuidelines the right context.
                if (guidelines?.length > 0) {
                    this.stage.guidelineManager.importGuidelines(guidelines);
                    this.stage.rulerCanvas?.update();
                }

                // Start a freshly loaded design on the first regular page
                // after page 0. Template pages are intentionally skipped.
                const firstRegularPage = this.layer.children
                    .filter(page => page.obj === 'page' && page.haspid > 0)
                    .sort((a, b) => a.haspid - b.haspid)[0];
                const pageAfterPageZero = firstRegularPage ?? this.layer.pageZero ?? this.layer.activePage;
                if (pageAfterPageZero) {
                    this.layer.switchToPage(pageAfterPageZero);
                    this.activePage = pageAfterPageZero;
                    this.selectedNode = pageAfterPageZero;
                }

                this.centerPlate();
                // Wait briefly for async image loads to settle, then surface any issues
                setTimeout(() => {
                    endImportSession();
                    const issues = getImportIssues();
                    if (issues.length > 0) {
                        this.importIssues = issues;
                        this.showImportIssuesPopup = true;
                    }
                }, 800);
            } catch (error) {
                endImportSession();
                console.error('Error during package import:', error);
                this.loadingMessage = 'Import failed';
                this.isLoading = false;
            } finally {
                // Update page visibility after import
                if (this.layer && this.layer.singlePageMode) {
                    this.layer.updatePageVisibility();
                }
                this.treeKey++;
                this._suppressSnapshot = false;
                this.loadingProgress = 1;
                this.isLoading = false;
                useUndoStore().clear();
                this.markDesignSaved();
            }
        },
        addPage(e) {
            const pobj = this.layer.addPage({ obj: 'page' });
            this.activePage = pobj;
            this.status = pobj.obj + pobj.haspid + ' added';
            this.treeKey++;
            useUndoStore().push(new PageCreateCommand({
                pageUuid: pobj.id(),
                pageHaspId: pobj.haspid,
                pageObj: 'page',
            }));
        },
        addTemplatePage(targetPageId) {
            const pobj = this.layer.addPage({
                obj:        'template_page',
                targetPage: targetPageId,
            });

            // Keep the tree selection in sync with the newly created active
            // page. Otherwise the palette still evaluates the previous
            // page/object as the selected parent and blocks the first root.
            if (this.selectedNode) this.selectedNode.selected = false;
            this.activePage = pobj;
            pobj.selected = true;
            this.selectedNode = pobj;
            this.selectedNodes = [pobj];
            this.status = 'Template page (→ P' + targetPageId + ') added';
            this.treeKey++;
            useUndoStore().push(new PageCreateCommand({
                pageUuid: pobj.id(),
                pageHaspId: pobj.haspid,
                pageObj: 'template_page',
            }));
        },
        handlePageSelect(page) {
            if (this.layer && page) {
                this.layer.switchToPage(page);
                this.selectedNode = page;
                this.treeKey++;
                this.statusKey++;
            }
        },
        handlePageDelete(page) {
            if (!this.layer || !page) return;
            if (page.haspid === 0) return; // page 0 is never deletable

            // Find all non-zero pages
            const allPages = this.layer.children.filter(child => child.obj === 'page' && child.haspid !== 0);
            
            // Don't delete if it's the last page
            if (allPages.length <= 1) {
                this.status = 'Cannot delete the last page';
                return;
            }
            
            // If deleting the active page, switch to another page first
            if (this.layer.activePage && this.layer.activePage._id === page._id) {
                // Find another page to switch to
                const otherPage = allPages.find(p => p._id !== page._id);
                if (otherPage) {
                    this.layer.switchToPage(otherPage);
                }
            }
            
            // Capture snapshot before destruction
            const pageUuid = page.id();
            const pageHaspId = page.haspid;
            const pageObj = page.obj;
            const pageLines = snapshotPage(page);

            // Destroy the page
            page.destroy();

            // Update UI
            this.selectedNode = this.layer.activePage;
            this.treeKey++;
            this.statusKey++;
            this.status = 'Page ' + pageHaspId + ' deleted';
            useUndoStore().push(new PageDeleteCommand({ pageUuid, pageHaspId, pageObj, pageLines }));
        },
        handlePageReorder(newOrder) {
            if (!this.layer) return;
            this.layer.reorderPages(newOrder);
            useUndoStore().clear();
            this.treeKey++;
            this.statusKey++;
            this.status = 'Pages reordered';
            this.markDesignDirty();
        },
        /** Select a node or wire of a graph domain (null kind clears it). */
        selectGraph(graphKey, kind, id) {
            const store = CANVAS_GRAPHS[graphKey].useStore();
            if (!kind) {
                store.clearSelection();
                return;
            }
            store.select(this.layer?.activePage, kind, id);
            // Object and graph-node selection are mutually exclusive.
            this.layer?.transformer?.nodes([]);
            this.layer?.batchDraw();
            this.rightPanelTab = 'properties';
        },
        /** Palette click: next to the selected node or object (see CANVAS_GRAPHS anchorsObject). */
        addGraphNode(graphKey, entry) {
            const page = this.layer?.activePage;
            if (!page) return;
            const graph = CANVAS_GRAPHS[graphKey];
            const store = graph.useStore();
            const selection = store.selection?.pageUuid === page.id() ? store.selection : null;
            const object = !selection && this.selectedNode &&
                getObjectPage(this.selectedNode) === page && graph.anchorsObject(entry, this.selectedNode)
                ? this.selectedNode : null;
            const { error } = store.addPaletteNode(page, entry, {
                anchorObject: object ? { uuid: object.id(), rect: graph.layerOf(this.stage).objectPageRect(object) } : null,
                anchorNodeId: selection?.kind === 'node' ? selection.id : null,
                plateWidth: this.layer.pageWidth,
                plateHeight: this.layer.pageHeight,
            });
            this.status = error ?? `${entry.label} added`;
            this.rightPanelTab = 'properties';
        },
        /** Arrange the active page's nodes of the graph that is showing (Logic or HA) around the plate. */
        arrangeGraph() {
            const graph = CANVAS_GRAPHS[this.canvasMode];
            const input = graph?.layerOf(this.stage)?.arrangeInput();
            if (!input) return;
            const { page, ...layout } = input;
            const command = graph.useStore().arrange(page, {
                ...layout,
                plate: { width: this.layer.pageWidth, height: this.layer.pageHeight },
            });
            this.status = command ? 'Nodes arranged' : 'Nodes already arranged';
        },
        /** Node picker choice: create the node where the wire was released and connect it. */
        pickGraphNode(entry) {
            const { page, dir, endpoint, point, graphKey } = this.graphPicker;
            this.graphPicker = null;
            const graph = CANVAS_GRAPHS[graphKey];
            const position = {
                // A wire drawn backwards ends at the new node's output, on its right edge.
                x: dir === 'out' ? point.x : point.x - graph.nodeSize.width,
                y: point.y - graph.nodeSize.height / 2,
            };
            const placement = dir === 'in' ? { position, connectTo: endpoint }
                : endpoint.object !== undefined ? { position, anchorObject: { uuid: endpoint.object } }
                    : { position, anchorNodeId: endpoint.node };
            const { error } = graph.useStore().addPaletteNode(page, entry, placement);
            this.status = error ?? `${entry.label} added`;
            this.rightPanelTab = 'properties';
        },
        onGraphDragOver(event) {
            if (!canvasGraphOfDrag(event.dataTransfer)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        },
        /** Palette drop: at the pointer, wired to what is under it (see CANVAS_GRAPHS dropAnchors). */
        onGraphDrop(event) {
            const graph = canvasGraphOfDrag(event.dataTransfer);
            const id = graph ? event.dataTransfer.getData(graph.mime) : '';
            if (!id) return;
            event.preventDefault();
            const store = graph.useStore();
            const entry = findPaletteEntry(store.palette, id);
            const target = entry ? graph.layerOf(this.stage)?.dropTarget(event) : null;
            if (!target) return;
            const { error } = store.addPaletteNode(target.page, entry, {
                position: { x: target.point.x, y: target.point.y - graph.nodeSize.height / 2 },
                ...graph.dropAnchors(entry, target),
            });
            this.status = error ?? `${entry.label} added`;
            this.rightPanelTab = 'properties';
        },
        switchToPageForObject(node) {
            if (!this.layer || !this.layer.singlePageMode || !node) return;
            
            // Get the page that contains this object
            const objectPage = getObjectPage(node);
            
            // If the object belongs to a page and it's not the current active page, switch to it
            if (objectPage && this.layer.activePage && objectPage._id !== this.layer.activePage._id) {
                this.layer.switchToPage(objectPage);
                this.treeKey++; // Update the page selector
            }
        },
        addObject(obj) {
            var newObject = null;
            if (obj === 'page') {
                newObject = this.addPage(); // addPage handles its own snapshot
            } else {
                newObject = this.layer.addObject({ obj: obj });
                if (newObject) {
                    const parent = newObject.getParent();
                    const parentWidth = parent?.width?.();
                    const parentHeight = parent?.height?.();
                    const objectWidth = newObject.width();
                    const objectHeight = newObject.height();
                    if (!newObject.isType('tab') && [parentWidth, parentHeight, objectWidth, objectHeight].every(Number.isFinite)) {
                        newObject.position({
                            x: Math.max(0, Math.round((parentWidth - objectWidth) / 2)),
                            y: Math.max(0, Math.round((parentHeight - objectHeight) / 2)),
                        });
                    }
                    const page = getObjectPage(newObject);
                    const config = snapshotObjectConfig(newObject);
                    config.page = page?.haspid;
                    useUndoStore().push(new ObjectCreateCommand({
                        objectUuid: newObject.id(),
                        config,
                        pageHaspId: page?.haspid,
                        pageUuid: page?.id(),
                    }));
                }
            }
            if (newObject) this.status = newObject.obj + newObject.haspid + ' added';
            this.treeKey++;
        },
        canDelete() {
            if (this.selectedNode !== undefined) return selectedNode.no_delete;
            return false;
        },
        deleteObject(e) {
            if (this.selectedNode !== undefined) {
                this.layer.deleteSelectedNodes(this.selectedNode);
                this.selectedNode = undefined;
                this.selectedNodes = [];
                this.treeKey++;
                this.statusKey++;
            }
        },
        stageCallback(data) {
            if (data.scale) this.stageScale = data.scale;
        },
        // Layercallback updates components with new node data
        layerCallback(event, node) {
            this.layerUpdate++;
            // Page switches, moves and edits can all move graph wire endpoints.
            this.stage?.logicLayer?.requestRender();
            this.stage?.hassLayer?.requestRender();
            // if (event === 'mouse-up') {

            // } else
            if (event === 'mouse-down') {
                // Object and graph-node selection are mutually exclusive.
                useLogicStore().clearSelection();
                useHassFlowStore().clearSelection();
                node.expanded = true;
                if (this.selectedNode) this.selectedNode.selected = false; //unselect current node
                node.selected = true; //select new node
                //here we get all parents and expand them all in the treeview
                const parents = this.getAllParents(node);
                parents.map((element) => {
                    element.expanded = true; //expand parent node
                });
                this.selectedNode = node;
                this.selectedNodes = this.layer ? [...this.layer.getSelectedObjects()] : [node];
                this.rightPanelTab = 'properties';
                // Switch to the page containing the selected object
                this.switchToPageForObject(node);
                // this.treeKey++; //trigger treeview redraw
                this.statusKey++; //trigger statubar redraw
            } else if (event === 'node-selected') {
                if (this.selectedNode) this.selectedNode.selected = false;
                node.selected = true;
                this.selectedNode = node;
                this.selectedNodes = [node];
                // Switch to the page containing the selected object
                this.switchToPageForObject(node);
                this.statusKey++;
                // Re-render tree so the selected highlight and auto-scroll fire
                this.treeKey++;
            } else if (event === 'object-converted') {
                this.selectedNode = node;
                this.selectedNodes = [node];
                this.treeKey++;
                this.statusKey++;
                this.updateNodeKey++;
            } else if (event === 'off-page-recovery-selected') {
                if (this.selectedNode) this.selectedNode.selected = false;
                this.selectedNode = undefined;
                this.selectedNodes = [];
                this.status = 'recovery: ' + node.obj + node.haspid;
                this.treeKey++;
                this.statusKey++;
            } else if (event === 'off-page-drag-move') {
                this.status = 'recovery: ' + node.obj + node.haspid;
                this.statusKey++;
            } else if (event === 'off-page-drag-end') {
                this.status = 'recovered: ' + node.obj + node.haspid;
                this.treeKey++;
                this.statusKey++;
            } else if (
                event === 'node-comment' ||
                event === 'node-haspid' ||
                event === 'node-moved' ||
                event === 'drag-end' ||
                event === 'geom-changed' ||
                event === 'image-loaded' ||
                event === 'button-drag-end' ||
                event === 'text_text'
            ) {
                if (this.appSettings.ui.page0_overlay) {
                    //update treeview
                    this.layer.updatePageZeroOverlay(event);
                }
                this.treeKey++;
                this.statusKey++;
                if (event === 'drag-end' || event === 'button-drag-end' || event === 'geom-changed' || event === 'image-loaded') {
                    this.updateNodeKey++;
                }
            } else if (event === 'hasp_angle') {
                if (node.rotation() < 0)
                    this.selectedNode.hasp_angle = Math.round(360 + node.rotation());
                else this.selectedNode.hasp_angle = Math.round(node.rotation());
            } else if (event === 'page-switch') {
                this.activePage = node;
                this.treeKey++;
            } else if (event === 'overlay-updated') {
                this.treeKey++;
            } else if (event === 'add-object') {
                if (node && node.obj !== 'page' && node.obj !== 'template_page' &&
                    getObjectPage(node)?.obj === 'template_page') {
                    if (this.selectedNode) this.selectedNode.selected = false;
                    node.selected = true;
                    this.selectedNode = node;
                    this.selectedNodes = [node];
                }
                this.treeKey++;
                this.statusKey++;
            } else if (event === 'add-object-error') {
                this.status = node?.message ?? 'Object cannot be added here';
            } else if (event === 'object-move-error') {
                this.status = node?.message ?? 'Object cannot be moved here';
            } else if (
                event === 'dataset' ||
                event === 'drag-move' ||
                event === 'transform-start' ||
                event === 'transform-end'
                //  ||
                // event === 'set-swipe' ||
                // event === 'node-action' ||
                // event === 'node-command' ||
                // event === 'node-hass-entityid' ||
                // event === 'node-hass-event' ||
                // event === 'node-hass-service'
            ) {
                if (event === 'dataset') this.markDesignDirty();
                this.statusKey++;
                if (event === 'transform-end') {
                    this.updateNodeKey++;
                }
            }
            if (node && !String(event).startsWith('off-page-') &&
                event !== 'add-object-error' && event !== 'object-move-error') {
                this.status = 'selected: ' + node.obj + node.haspid;
            }

            // Command-based history — skip pure UI/in-progress events
            const NO_HISTORY = new Set([
                'mouse-down', 'mouse-up', 'node-selected', 'drag-move',
                'transform-start', 'page-switch', 'overlay-updated',
                'image-loaded', 'add-object-error', 'object-move-error', 'add-object', 'dataset',
                'set-enabled', 'object-converted', 'off-page-recovery-selected', 'off-page-drag-move',
            ]);
            const IMMEDIATE_COMMAND = new Set([
                'drag-end', 'button-drag-end', 'transform-end', 'node-moved',
                'hass-config-changed', 'gpio-bindings-changed', 'multi-drag-end', 'off-page-drag-end',
            ]);
            if (!NO_HISTORY.has(event) && !this.layer?._historySuppress) {
                const LABEL_MAP = {
                    'drag-end': 'Move', 'button-drag-end': 'Move',
                    'transform-end': 'Resize', 'node-moved': 'Move',
                    'geom-changed': 'Move',
                    'node-comment': 'Edit comment', 'node-haspid': 'Edit ID',
                    'text_text': 'Edit text', 'hasp_angle': 'Set angle',
                    'hass-config-changed': 'HA Config Changed',
                    'gpio-bindings-changed': 'GPIO Bindings Changed',
                };
                const label = LABEL_MAP[event] || 'Edit property';
                if (IMMEDIATE_COMMAND.has(event)) {
                    clearTimeout(this._propChangeTimer);
                    this._propChangeTimer = null;

                    if (event === 'multi-drag-end' && Array.isArray(node)) {
                        const preDragPositions = this.layer?._multiDragPositions;
                        if (preDragPositions) {
                            const moves = node
                                .filter(o => preDragPositions.has(o.id()))
                                .map(o => {
                                    const pre = preDragPositions.get(o.id());
                                    return new ObjectMoveCommand({
                                        description: 'Move',
                                        objectUuid: o.id(),
                                        pageUuid: getObjectPage(o)?.id(),
                                        prevX: pre.x, prevY: pre.y,
                                        finalX: o.x(), finalY: o.y(),
                                    });
                                })
                                .filter(cmd => cmd.prevX !== cmd.finalX || cmd.prevY !== cmd.finalY);
                            if (moves.length > 0) {
                                useUndoStore().push(new BatchCommand(moves, 'Move group'));
                            }
                        }
                        this.treeKey++;
                        this.statusKey++;
                    } else if ((event === 'drag-end' || event === 'button-drag-end' || event === 'off-page-drag-end') && node) {
                        const pre = this.layer?._preDragPos;
                        if (pre) {
                            useUndoStore().push(new ObjectMoveCommand({
                                description: label,
                                objectUuid: node.id(),
                                pageUuid: getObjectPage(node)?.id(),
                                prevX: pre.x,
                                prevY: pre.y,
                                finalX: node.x(),
                                finalY: node.y(),
                            }));
                        }
                    } else if (event === 'transform-end' && node) {
                        const pre = this.layer?._preTransformState;
                        if (pre) {
                            useUndoStore().push(new ObjectResizeCommand({
                                objectUuid: node.id(),
                                pageUuid: getObjectPage(node)?.id(),
                                prevX: pre.x, prevY: pre.y,
                                prevW: pre.w, prevH: pre.h,
                                finalX: node.x(), finalY: node.y(),
                                finalW: node.width(), finalH: node.height(),
                            }));
                        }
                    } else if (event === 'geom-changed' && node) {
                        const pre = this.layer?._preGeomState;
                        if (pre) {
                            useUndoStore().push(new ObjectResizeCommand({
                                objectUuid: node.id(),
                                pageUuid: getObjectPage(node)?.id(),
                                prevX: pre.x, prevY: pre.y,
                                prevW: pre.w, prevH: pre.h,
                                finalX: node.x(), finalY: node.y(),
                                finalW: node.width(), finalH: node.height(),
                            }));
                        }
                    } else if ((event === 'hass-config-changed' || event === 'gpio-bindings-changed') && node) {
                        const beforeColors = this.layer?._beforeColors;
                        const prevHass = beforeColors?.['hass_config'];
                        const prevGpio = beforeColors?.['gpio_bindings'];
                        if (this.layer) this.layer._beforeColors = null;
                        const prevState = capturePropertyState(node);
                        prevState.hass_config = prevHass ?? null;
                        prevState.gpio_bindings = prevGpio ?? null;
                        const nextState = capturePropertyState(node);
                        const cmd = new PropertyChangeCommand({
                            objectUuid: node.id(),
                            pageUuid: getObjectPage(node)?.id(),
                            prevState,
                            nextState,
                        });
                        useUndoStore().push(cmd);
                    } else {
                        // node-moved (tree reparent): not yet command-based, no-op
                    }
                } else {
                    // Debounced property change — capture before-state on first event in window
                    if (!this._propChangeTimer && node) {
                        const beforeState = capturePropertyState(node);
                        // For color changes, the property editor stored the before-value
                        // on layer._beforeColors before applying the change — use it.
                        // Keys are "part:prop" (default-state styles), "part:state:prop"
                        // (non-default state overrides — see PartPropertiesPanel.vue),
                        // "hass_config", or some other bare label (kept as a top-level
                        // property same as before).
                        const beforeColors = this.layer?._beforeColors;
                        if (beforeColors) {
                            for (const [key, val] of Object.entries(beforeColors)) {
                                const sep = key.indexOf(':');
                                if (key === 'hass_config') {
                                    beforeState.hass_config = val;
                                } else if (sep !== -1) {
                                    const segments = key.split(':');
                                    const [part, stateName, prop] = segments.length === 3
                                        ? segments
                                        : [segments[0], 'default', segments[1]];
                                    beforeState.styles[part] ??= { states: {} };
                                    beforeState.styles[part].states[stateName] ??= { properties: {} };
                                    beforeState.styles[part].states[stateName].properties[prop] = val;
                                } else {
                                    beforeState.properties[key] = val;
                                }
                            }
                            this.layer._beforeColors = null;
                        }
                        this._propChangeBefore = { uuid: node.id(), state: beforeState, pageUuid: getObjectPage(node)?.id() };
                    }
                    clearTimeout(this._propChangeTimer);
                    this._propChangeTimer = setTimeout(() => {
                        this._propChangeTimer = null;
                        const before = this._propChangeBefore;
                        this._propChangeBefore = null;
                        if (before && node) {
                            useUndoStore().push(new PropertyChangeCommand({
                                objectUuid: before.uuid,
                                pageUuid: before.pageUuid,
                                prevState: before.state,
                                nextState: capturePropertyState(node),
                            }));
                        }
                    }, 800);
                }
            }
        },
        // The one place the canvas is resized to its container. Called by the
        // container's ResizeObserver, so it must be idempotent: identical sizes
        // do nothing at all, which keeps a fractional container width from
        // oscillating between two layouts. Zoom never comes through here —
        // zooming only changes the stage's scale/position, never any DOM box.
        fitStageIntoContainer() {
            const container = this.container ?? document.querySelector('#konva_container');
            if (!container || !this.stage) return;
            const width = Math.floor(container.clientWidth);
            const height = Math.floor(container.clientHeight);
            if (width <= 0 || height <= 0) return;
            if (width === this.stage.width() && height === this.stage.height()) return;
            this.stage.width(width);
            this.stage.height(height);
            this.layer?.updateOffPageMask?.();
            this.stage.rulerCanvas?.resize();
        },
        addServerNode(n) {
            if (n.children && n.children.length > 0) return;
            const id = `${Date.now()}`;
            const newNode = {
                text: `loaded from server`,
                children: [],
                state: {},
            };
            // add the node to nodes
            this.nodes[id] = newNode;
            // set children
            n.children = [id];
            // end loading
            n.state.isLoading = false;
        },
        onUpdatePlates(payload) {
            saveAllPlates(payload);
        },
        handleImageDeleted(id) {
            this.imageStoreUpdated++;
        },
        handleImageAdded() {
            this.imageStoreUpdated++;
        },
        handleDeleteButton() {
            if (this.selectedNode !== undefined && !this.selectedNode.no_delete) {
                if (this.selectedNode.isType('mtrx_btn')) {
                    const matrix = this.selectedNode.button_matrix;
                    matrix.removeButton(this.selectedNode);
                    matrix.drawButtons();
                    this.layerUpdate++;
                    this.treeKey++;
                    // TODO: BatchCommand for matrix button delete
                } else {
                    this.showDeleteModal = true;
                }
            }
        },
        deleteItem() {
            const node = this.selectedNode;
            if (node) {
                const page = getObjectPage(node);
                const config = snapshotObjectConfig(node);
                config.page = page?.haspid;
                const uuid = node.id();
                const pageHaspId = page?.haspid;
                const pageUuid = page?.id();
                // Graph nodes keep existing; only their wires to deleted objects go.
                const doomed = this.layer.selectedObjects.length > 0 ? [...this.layer.selectedObjects] : [node];
                const graphCommands = [
                    ...useLogicStore().detachObjects(doomed, getObjectPage),
                    ...useHassFlowStore().detachObjects(doomed, getObjectPage),
                ];
                this.deleteObject();
                const deleteCommand = new ObjectDeleteCommand({ objectUuid: uuid, config, pageHaspId, pageUuid });
                useUndoStore().push(graphCommands.length
                    ? new BatchCommand([deleteCommand, ...graphCommands], deleteCommand.description)
                    : deleteCommand);
            }
            this.showDeleteModal = false;
        },
        cancelDelete() {
            this.showDeleteModal = false;
        },
        // cancelExport() {
        //     this.showExportModal = false;
        // },
        handleCopyObject() {
            const copiedUuids = new Map();
            const createdObjects = this.layer.copySelectedObject(copiedUuids);
            this.status = 'object copied';
            this.treeKey++;
            if (createdObjects?.length) {
                const commands = createdObjects.map(obj => {
                    const page = getObjectPage(obj);
                    const config = snapshotObjectConfig(obj);
                    config.page = page?.haspid;
                    return new ObjectCreateCommand({ objectUuid: obj.id(), config, pageHaspId: page?.haspid });
                });
                commands.push(...copyObjectLogic(this.layer, copiedUuids, useLogicStore(), { getPage: getObjectPage }));
                commands.push(...copyObjectHass(this.layer, copiedUuids, useHassFlowStore(), { getPage: getObjectPage }));
                const cmd = commands.length === 1
                    ? commands[0]
                    : new BatchCommand(commands, 'Copy object');
                cmd.description = 'Copy object';
                useUndoStore().push(cmd);
            }
        },
        pageScreenshot() {
            var dataURL = this.selectedNode.toDataURL();

            // Create a link element
            var link = document.createElement('a');
            link.href = dataURL;
            link.download = 'konva_screenshot.png';

            // Programmatically click the link to trigger the download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        async newProject() {
            this._suppressSnapshot = true;
            this.stage.guidelineManager.clearGuidelines();
            this.layer.clearAll();
            this.layer.destroy();
            this.addNewLayer();

            this.designId = uuidv4();
            this.currentPackage = null;
            this.designName = '';
            this.designMajor = 0;
            this.designMinor = 0;
            this.designVersionHistory = [];

            this.selectedNode = undefined;
            await this.$nextTick();
            this.layer.addPage({ obj: 'page', page: 0 });
            this.addPage();
            this.centerPlate();
            await this.$nextTick();
            this.showNewProjectModal = false;
            useUndoStore().clear();
            useBookmarkStore().clear();
            this._suppressSnapshot = false;
            this.markDesignSaved();
        },
        handleCanvasKey(event) {
            // if (this.container === document.activeElement) {
            var shortcutkey = '';
            if (event.ctrlKey) {
                shortcutkey += 'ctrl-';
            }
            if (event.altKey) {
                shortcutkey += 'alt-';
            }
            if (event.shiftKey) {
                shortcutkey += 'shift-';
            }
            shortcutkey += event.key;

            if (event.key === 'Tab' && event.shiftKey && !event.ctrlKey && !event.altKey) {
                // Shift+Tab cycles Design → Logic → HA instead of moving focus.
                event.preventDefault();
                useCanvasModeStore().cycleMode();
            } else if (event.code === 'KeyH' && event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
                // Shift+H toggles the Home Assistant flow view (layout-independent key).
                event.preventDefault();
                useCanvasModeStore().toggleMode('hass');
            } else if (event.code === 'KeyL' && event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey
                && CANVAS_GRAPHS[this.canvasMode]) {
                // Shift+L arranges the nodes of the graph mode that is active.
                event.preventDefault();
                this.arrangeGraph();
            } else if ((event.key === 'Delete' || event.key === 'Backspace') && useLogicStore().selection) {
                event.preventDefault();
                useLogicStore().deleteSelection(this.layer?.activePage);
            } else if ((event.key === 'Delete' || event.key === 'Backspace') && useHassFlowStore().selection) {
                event.preventDefault();
                useHassFlowStore().deleteSelection(this.layer?.activePage);
            } else if (event.key === 'Delete') {
                this.showDeleteModal = true;
            } else if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                this.undo();
            } else if (event.ctrlKey && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
                event.preventDefault();
                this.redo();
            } else if (shortcutLookup[shortcutkey] !== undefined) {
                // this.addObject(shortcutLookup[shortcutkey]);
            }
            // }
            // event.preventDefault();
        },
        getAllParents(node, parents = []) {
            const parent = node.getParent();
            if (parent) {
                parents.push(parent);
                return this.getAllParents(parent, parents);
            }
            return parents;
        },

        async loadCustomComponents() {
            this.customComponents = await getAllCustomComponents();
        },
        async openSaveCustomComponentDialog() {
            this.saveCustomComponentThumbnail = await this.layer.exportSelectionThumbnail();
            this.showSaveCustomComponentDialog = true;
        },
        async handleSaveCustomComponent({ name, description, icon }) {
            const objects = this.layer.exportSelectionAsTemplate();
            if (!objects || objects.length === 0) {
                alert('Nothing to save. Select an object first.');
                return;
            }
            const component = {
                id: uuidv4(),
                name,
                description,
                icon,
                created: new Date().toISOString(),
                objects,
            };
            Object.assign(component, this.componentGraphs(this.layer.haspobject));
            await addCustomComponent(component);
            this.showSaveCustomComponentDialog = false;
            await this.loadCustomComponents();
            this.customManagerRefresh++;
        },
        /** The graphs (logic, hass) of a component's object tree, for saving it; empty ones are left out. */
        componentGraphs(root) {
            const graphs = {};
            const logic = exportObjectTreeLogic(root, getObjectPage);
            if (logic) graphs.logic = logic;
            const hass = exportObjectTreeHass(root, getObjectPage);
            if (hass) graphs.hass = hass;
            return graphs;
        },
        placeCustomComponent(component) {
            if (!this.layer) return;
            this.layer.placeCustomComponent(component);
            this.treeKey++;
            this.statusKey++;
            this.markDesignDirty();
            // TODO: ObjectCreateCommand for placed component
        },
        startEditComponentDesign(component) {
            if (!this.layer) return;
            this.layer.placeCustomComponent(component);
            this.treeKey++;
            this.statusKey++;
            this.editingComponent = component;
            this.showCustomManagerPopup = false;
        },
        async handleUpdateCustomComponentDesign() {
            const objects = this.layer.exportSelectionAsTemplate();
            if (!objects || objects.length === 0) {
                alert('Nothing selected. Select the component root object on the canvas first.');
                return;
            }
            const thumbnail = await this.layer.exportSelectionThumbnail();
            const { logic: _logic, hass: _hass, ...component } = this.editingComponent;
            const updated = {
                ...component,
                objects,
                ...this.componentGraphs(this.layer.haspobject),
                icon: thumbnail ?? this.editingComponent.icon,
                updated: new Date().toISOString(),
            };
            await updateCustomComponent(updated);
            this.editingComponent = null;
            await this.loadCustomComponents();
            this.customManagerRefresh++;
        },
    },
};
</script>

<style>
/* @import url('https://fonts.googleapis.com/css2?family=Roboto&display=swap'); */
/* @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200'); */

:host {
    font-family: Arial, Helvetica, sans-serif;
}

.container {
    position: relative;
    width: 50%;
}

.image {
    display: block;
    width: 100%;
    height: auto;
}

#konva_container:focus {
    outline: none;
}

.overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    transition: 0.3s ease;
    background-color: #275bec;
    color: white;
    border-radius: 10px;
}

.container:hover .overlay {
    opacity: 0.8;
}
</style>
